import {
  getBanners,
  getConfirmationScreens,
  getDepartments,
  getMaintenanceSettings,
  getProducts
} from 'lib/woocomerce';
import { OrderEnum, ProductsOrderByEnum } from 'lib/woocomerce/__generated__/graphql';
import { Banner, ConfirmationScreenMap, Department, Product } from 'lib/woocomerce/types';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useEffect } from 'react';

import { CollectionView, Header, Layout, Overlay } from 'src/components';
import { Background } from 'src/components/background';
import { ProductsProvider } from 'src/context/products-context';
import { StoreProvider } from 'src/context/store-context';
import { useTrackPageView } from 'src/hooks/use-track-page-view';
import { storefrontName } from 'src/utils';

type PageProps = {
  banner: Banner;
  initialProducts: Product[];
  confirmationScreens: ConfirmationScreenMap;
  departments: Department[];
};

const Content = () => {
  useTrackPageView();

  useEffect(() => {
    // Check if running in iframe
    const isInIframe = window.self !== window.top;

    if (isInIframe) {
      // Listen for messages from login page
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          console.warn('Ignoring message from untrusted origin:', event.origin);
          return;
        }

        if (event.data.type === 'LOGIN_WINDOW_OPENED') {
          // Get localStorage data
          const localStorageData: Record<string, string | null> = {};
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
              localStorageData[key] = localStorage.getItem(key);
            }
          }

          // Send data to login page
          event.source?.postMessage(
            {
              type: 'LOCALSTORAGE_DATA',
              localStorageData
            },
            { targetOrigin: window.location.origin }
          );
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  return (
    <>
      <Head>
        <title>{storefrontName}</title>
      </Head>
      <Background />
      <Layout>
        <Header />
        <CollectionView />
      </Layout>
      <Overlay />
    </>
  );
};
export default function Index({
  banner,
  initialProducts,
  confirmationScreens,
  departments
}: PageProps) {
  return (
    <StoreProvider value={{ banner, confirmationScreens, departments }}>
      <ProductsProvider value={{ initialProducts }}>
        <Content />
      </ProductsProvider>
    </StoreProvider>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  context.res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=10');

  const maintenanceSettings = await getMaintenanceSettings();
  if (maintenanceSettings?.enabled) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false
      }
    };
  }

  const banner = (await getBanners({ first: 1, search: '[active]' })).pop() || null;
  const departments = (await getDepartments()) || null;
  const initialProducts = await getProducts({
    first: 1000,
    orderby: [{ order: OrderEnum.Asc, field: ProductsOrderByEnum.MenuOrder }]
  });

  const confirmationScreens = await getConfirmationScreens({ first: 4 });

  return {
    props: {
      banner,
      departments,
      initialProducts,
      confirmationScreens
    }
  };
}
